// Must stay first: wraps fetch so backend requests carry X-TOL-Client.
import './service/installTolClientFetch';
import React from 'react';
import ReactDOM from 'react-dom';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { Provider } from 'react-redux';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  useLocation,
} from 'react-router-dom';
import GA4React from 'ga-4-react';

import runtimeEnv from '@mars/heroku-js-runtime-env';
import store from './redux';
import { initAnalytics } from './service/analytics';
import PrivateRoute from './auth/PrivateLayout';
import StartPage from './page/StartPage';
import PressKitPage from './page/PressKitPage';
import PrivacyPage from './page/PrivacyPage';
import TermsPage from './page/TermsPage';
import DeleteAccountPage from './page/DeleteAccountPage';
import Blog from './page/Blog';
import RampComponent from './page/RampComponent';
import AuthLayout from './auth/AuthLayout';
import { testAuth } from './redux/actions/User';
import AppRecovery, { LoadingScreen } from './components/AppRecovery';
import { HelmetProvider } from 'react-helmet-async';
import ThemeRoot from './theme/ThemeRoot';
import * as serviceWorker from './serviceWorker';
import SignUp from './page/SignUp';
import SelectTeam from './page/SelectTeam';
import LoginCallback from './page/LoginCallback';
import PreviewCallback from './page/PreviewCallback';
import YouthCamp from './page/YouthCamp';
import YouthCampSignUp from './page/YouthCampSignUp';
import Rewards from './page/Rewards';
import PrivateAppShell from './auth/PrivateAppShell';
import DevMe25Gallery from './page/DevMe25Gallery';

import './index.css';
import './assets/style/index.scss';

const isDevGallery =
  process.env.NODE_ENV === 'development' ||
  process.env.REACT_APP_ME25_GALLERY === '1';

// const { REACT_APP_MIXPANEL_KEY, REACT_APP_SENTRY_DSN } = runtimeEnv();

// Optional analytics must not block startup: initAnalytics never throws, and
// registers the `platform: 'web'` super property right after init.
initAnalytics(process.env.REACT_APP_MIXPANEL_KEY, { ignore_dnt: true });
// Sentry Initialization
try {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    integrations: [new Integrations.BrowserTracing()],
    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control
    tracesSampleRate: 1.0,
  });
} catch {
  /* Optional reporting must not block startup. */
}

try {
  const ga4react = new GA4React('G-FFPJXZZSGC', 5000);
  ga4react.initialize().then(
    (ga4) => {
      ga4.pageview('path');
      ga4.gtag('event', 'pageview', 'path'); // or your custom gtag event
    },
    (err) => {
      console.error(err);
    }
  );
} catch {
  /* Optional analytics must not block startup. */
}

const Routes = () => (
  <Switch>
    <Route exact path="/signup" component={SignUp} />
    <Route exact path="/preview" component={PreviewCallback} />
    <Route path="/login/discord_callback" component={LoginCallback} />
    <Route exact path="/press-kit" component={PressKitPage} />
    <Route exact path="/privacy" component={PrivacyPage} />
    <Route exact path="/terms" component={TermsPage} />
    <Route exact path="/delete-account" component={DeleteAccountPage} />
    <Route exact path="/blog/:slug?" component={Blog} />
    <Route exact path="/test-ramp" component={RampComponent} />

    <Route exact path="/select-team/:leagueCodeParam?" component={SelectTeam} />
    <PrivateRoute path="/rewards" emptyLayout component={Rewards} />
    <PrivateRoute exact path="/youth-camp" emptyLayout component={YouthCamp} />
    <PrivateRoute
      exact
      path="/youth-camp-signup"
      emptyLayout
      component={YouthCampSignUp}
    />
    <Route
      path={[
        '/home',
        '/league',
        '/finance',
        '/team',
        '/manager',
        '/transfers',
        '/domestic-cup',
        '/cross-server',
        '/user-guide',
        '/stuff',
        '/_page_that_consist_discord',
      ]}
      component={PrivateAppShell}
    />
    <Route exact path="/" component={StartPage} />
    <Redirect to="/home" />
  </Switch>
);
function Application() {
  const location = useLocation();
  React.useEffect(() => {
    window.__TOL_APP_MOUNTED__ = true;
  }, []);
  // Local ME 2.5 visual gallery — skip auth shell so mock UI is reachable offline.
  if (isDevGallery && location.pathname.startsWith('/dev/me25')) {
    return <DevMe25Gallery />;
  }
  return (
    <AppRecovery
      key={location.pathname}
      onRecover={() => store.dispatch(testAuth())}
      fallback={<LoadingScreen />}
    >
      <AuthLayout>
        <Routes />
      </AuthLayout>
    </AppRecovery>
  );
}
ReactDOM.render(
  <React.StrictMode>
    <HelmetProvider>
      <Provider store={store}>
        <ThemeRoot>
          <Router>
            <Application />
          </Router>
        </ThemeRoot>
      </Provider>
    </HelmetProvider>
  </React.StrictMode>,
  document.getElementById('root')
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
